import { useState, useEffect, useMemo } from "react";
import type { JSX } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthV2";
import { useToast } from "@/hooks/use-toast";
import { Navigate } from "react-router-dom";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useModuleFlags } from "@/contexts/ModuleFlagsContext";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  ArrowLeft,
  Plus as PlusIcon, 
  Pencil as PencilIcon, 
  Trash2 as TrashIcon, 
  Database as DatabaseIcon, 
  Settings2 as Settings2Icon, 
  Users as UsersIcon,
  Layers as LayersIcon,
  Building2 as Building2Icon,
  Globe as GlobeIcon,
  Shield as ShieldIcon,
  ArrowRightLeft as ArrowRightLeftIcon,
  Folder as FolderIcon,
  MessageSquare,
  LayoutTemplate,
  FileText,
  Sparkles,
  Map as MapIcon,
  Bell,
  Bug,
  Compass,
  ImageIcon,
} from "lucide-react";
import DataExchangeTabs from "@/components/import-export/DataExchangeTabs";
import UserManagement from "@/components/UserManagement";
import ServiceIntegrationManagement from "@/components/ServiceIntegrationManagement";
import QuotaManagement from "@/components/QuotaManagement";
import DataAccessManagement from "@/components/DataAccessManagement";
import StorageManagement from "@/components/StorageManagement";
import ApiManagement from "@/components/ApiManagement";
import McpClientManagement from "@/components/McpClientManagement";
import BotSettingsManagement from "@/components/BotSettingsManagement";
import CardTemplatesHub from "@/components/admin/CardTemplatesHub";
import GoAdminImportConsole from "@/components/admin/GoAdminImportConsole";
import ReportsManager from "@/components/admin/ReportsManager";
import { ReferenceLayerManagement } from "@/components/admin/ReferenceLayerManagement";
import AiAgentsAdminPanel from "@/components/admin/AiAgentsAdminPanel";
import NotificationsAdmin from "@/components/admin/NotificationsAdmin";
import FeedbackAdmin from "@/components/admin/FeedbackAdmin";
import OnboardingAdminPanel from "@/components/admin/OnboardingAdminPanel";
import ChatGifsAdminPanel from "@/components/admin/ChatGifsAdminPanel";
import { SecurityFeatureFlagsPanel } from "@/components/admin/SecurityFeatureFlagsPanel";
import { ReferencesSectionHeader } from "@/components/admin/ReferencesSectionHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import QualTypeManagement from "@/components/KlsManagement/QualTypeManagement";
import QualManagement from "@/components/KlsManagement/QualManagement";
import KlsManagement from "@/components/KlsManagement/KlsManagement";
import ExternalSourcesManagement from "@/components/KlsManagement/ExternalSourcesManagement";
import SyncLogsView from "@/components/KlsManagement/SyncLogsView";
import { 
  objectTypesApi, 
  organizationsApi, 
  statsApi,
  rolesApi,
  mcpApi,
  adminApi,
} from "@/services/api-v2.service";
import type { 
  ObjectType, 
  ObjectTypeCreate,
  ObjectTypeUpdate,
  // OrgType удалены - организации теперь в KLS (qual_id = 336)
  Organization,
  SystemStats,
  Role,
  McpServerStatus
} from "@/types/api-v2";
import { buildObjectTypesHierarchy, flattenObjectTypesHierarchy } from "@/utils/objectTypesHierarchy";

export default function AdminPage() {
  const { user, isLoading, hasPermission } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { refresh: refreshModuleFlags } = useModuleFlags();
  const [activeTab, setActiveTab] = useState("references");
  const [activeReferenceTab, setActiveReferenceTab] = useState("object-types");
  
  // Dialog states for references section
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // isOrgTypeDialogOpen удален - типы организаций теперь не используются
  // isOrganizationDialogOpen удален - организации управляются через KLS
  const [editingItem, setEditingItem] = useState<any>(null);

  // Confirmation dialog state
  type ConfirmDialogState = {
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  };
  
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => {}
  });

  // MCP server state
  const [mcpActiveTab, setMcpActiveTab] = useState("server");
  const [mcpServerStatus, setMcpServerStatus] = useState<'stopped' | 'starting' | 'running' | 'error' | null>(null);
  const [mcpActiveConnections, setMcpActiveConnections] = useState(0);
  const [mcpStartLoading, setMcpStartLoading] = useState(false);
  const [isMcpGeoDialogOpen, setIsMcpGeoDialogOpen] = useState(false);
  const [isMcpObjectsDialogOpen, setIsMcpObjectsDialogOpen] = useState(false);
  const [isMcpAnalyticsDialogOpen, setIsMcpAnalyticsDialogOpen] = useState(false);

  // Fetch MCP server status
  const { data: mcpStatus } = useQuery<McpServerStatus>({
    queryKey: ["mcp", "status"],
    queryFn: () => mcpApi.getStatus(),
    enabled: activeTab === "mcp" && !!user && hasPermission("admin.access"),
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  // Update local state when status changes
  useEffect(() => {
    if (mcpStatus) {
      setMcpServerStatus(mcpStatus.status as any);
      setMcpActiveConnections(mcpStatus.activeConnections);
    }
  }, [mcpStatus]);

  // MCP server functions
  const handleStartMcpServer = async () => {
    setMcpStartLoading(true);
    try {
      const result = await mcpApi.start();
      toast({
        title: "MCP сервер запускается",
        description: result.message || "Model Context Protocol сервер инициализируется..."
      });
      queryClient.invalidateQueries({ queryKey: ["mcp", "status"] });
    } catch (error: any) {
      toast({
        title: "Ошибка запуска",
        description: error.message || "Не удалось запустить MCP сервер. Проверьте настройки и попробуйте снова.",
        variant: "destructive"
      });
    } finally {
      setMcpStartLoading(false);
    }
  };

  const handleStopMcpServer = async () => {
    setMcpStartLoading(true);
    try {
      const result = await mcpApi.stop();
      toast({
        title: "MCP сервер остановлен",
        description: result.message || "Model Context Protocol сервер успешно остановлен"
      });
      queryClient.invalidateQueries({ queryKey: ["mcp", "status"] });
    } catch (error: any) {
      toast({
        title: "Ошибка остановки",
        description: error.message || "Не удалось остановить MCP сервер",
        variant: "destructive"
      });
    } finally {
      setMcpStartLoading(false);
    }
  };

  /** Форма «Настройки системы» (синхронизируется с GET /admin/system-settings). */
  type SystemFormState = {
    app_display_name: string;
    map_default_zoom: number;
    map_default_center_lat: number;
    map_default_center_lng: number;
    allow_registration: boolean;
    auto_activate_new_users: boolean;
    require_email_verification: boolean;
    default_new_user_role_id: string;
    default_new_user_organization_id: string;
    module_oi_enabled: boolean;
    module_rd_enabled: boolean;
    module_go_enabled: boolean;
    module_udgspz_enabled: boolean;
    module_visualizer_enabled: boolean;
    module_tactical_enabled: boolean;
    module_work_items_enabled: boolean;
    module_feedback_enabled: boolean;
    module_onboarding_enabled: boolean;
    module_chat_enabled: boolean;
  };

  const [chatDisableConfirmOpen, setChatDisableConfirmOpen] = useState(false);

  const [systemForm, setSystemForm] = useState<SystemFormState>({
    app_display_name: "ГИС Объекты",
    map_default_zoom: 10,
    map_default_center_lat: 55.751244,
    map_default_center_lng: 37.618423,
    allow_registration: true,
    auto_activate_new_users: false,
    require_email_verification: false,
    default_new_user_role_id: "none",
    default_new_user_organization_id: "none",
    module_oi_enabled: true,
    module_rd_enabled: true,
    module_go_enabled: true,
    module_udgspz_enabled: true,
    module_visualizer_enabled: true,
    module_tactical_enabled: false,
    module_work_items_enabled: true,
    module_feedback_enabled: true,
    module_onboarding_enabled: true,
    module_chat_enabled: true,
  });

  const {
    data: sysSettings,
    isLoading: systemSettingsLoading,
    error: systemSettingsError,
  } = useQuery({
    queryKey: ["admin", "system-settings"],
    queryFn: () => adminApi.getSystemSettings(),
    enabled: !!user && hasPermission("admin.access") && activeTab === "settings",
  });

  useEffect(() => {
    if (!sysSettings) return;
    setSystemForm({
      app_display_name: sysSettings.app_display_name,
      map_default_zoom: sysSettings.map_default_zoom,
      map_default_center_lat: sysSettings.map_default_center_lat,
      map_default_center_lng: sysSettings.map_default_center_lng,
      allow_registration: sysSettings.allow_registration,
      auto_activate_new_users: sysSettings.auto_activate_new_users,
      require_email_verification: sysSettings.require_email_verification,
      default_new_user_role_id:
        sysSettings.default_new_user_role_id != null
          ? String(sysSettings.default_new_user_role_id)
          : "none",
      default_new_user_organization_id:
        sysSettings.default_new_user_organization_id != null
          ? String(sysSettings.default_new_user_organization_id)
          : "none",
      module_oi_enabled: sysSettings.module_oi_enabled ?? true,
      module_rd_enabled: sysSettings.module_rd_enabled ?? true,
      module_go_enabled: sysSettings.module_go_enabled ?? true,
      module_udgspz_enabled: sysSettings.module_udgspz_enabled ?? true,
      module_visualizer_enabled: sysSettings.module_visualizer_enabled ?? true,
      module_tactical_enabled: sysSettings.module_tactical_enabled ?? false,
      module_work_items_enabled: sysSettings.module_work_items_enabled ?? true,
      module_feedback_enabled: sysSettings.module_feedback_enabled ?? true,
      module_onboarding_enabled: sysSettings.module_onboarding_enabled ?? true,
      module_chat_enabled: sysSettings.module_chat_enabled ?? true,
    });
  }, [sysSettings]);

  const saveSettingsMutation = useMutation({
    mutationFn: () =>
      adminApi.updateSystemSettings({
        app_display_name: systemForm.app_display_name.trim(),
        map_default_zoom: systemForm.map_default_zoom,
        map_default_center_lat: systemForm.map_default_center_lat,
        map_default_center_lng: systemForm.map_default_center_lng,
        allow_registration: systemForm.allow_registration,
        auto_activate_new_users: systemForm.auto_activate_new_users,
        require_email_verification: systemForm.require_email_verification,
        default_new_user_role_id:
          systemForm.default_new_user_role_id === "none"
            ? null
            : parseInt(systemForm.default_new_user_role_id, 10),
        default_new_user_organization_id:
          systemForm.default_new_user_organization_id === "none"
            ? null
            : parseInt(systemForm.default_new_user_organization_id, 10),
        module_oi_enabled: systemForm.module_oi_enabled,
        module_rd_enabled: systemForm.module_rd_enabled,
        module_go_enabled: systemForm.module_go_enabled,
        module_udgspz_enabled: systemForm.module_udgspz_enabled,
        module_visualizer_enabled: systemForm.module_visualizer_enabled,
        module_tactical_enabled: systemForm.module_tactical_enabled,
        module_work_items_enabled: systemForm.module_work_items_enabled,
        module_feedback_enabled: systemForm.module_feedback_enabled,
        module_onboarding_enabled: systemForm.module_onboarding_enabled,
        module_chat_enabled: systemForm.module_chat_enabled,
      }),
    onSuccess: async () => {
      toast({
        title: "Настройки сохранены",
        description: "Параметры записаны в базу данных.",
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "system-settings"] });
      await refreshModuleFlags();
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось сохранить настройки",
        variant: "destructive",
      });
    },
  });


  // Data fetching
  const { data: stats, isLoading: isLoadingStats } = useQuery<SystemStats>({
    queryKey: ["stats"],
    queryFn: () => statsApi.getSystemStats(),
    refetchInterval: 30000,
  });

  const { data: objectTypes = [], isLoading: isLoadingObjectTypes } = useQuery<ObjectType[]>({
    queryKey: ["object-types"],
    queryFn: () => objectTypesApi.list(),
    enabled: !!user && hasPermission("admin.access"),
  });
  
  // Строим иерархию типов объектов для отображения в select
  const objectTypesHierarchy = useMemo(() => {
    if (!objectTypes || objectTypes.length === 0) return [];
    const hierarchy = buildObjectTypesHierarchy(objectTypes);
    return flattenObjectTypesHierarchy(hierarchy);
  }, [objectTypes]);

  // orgTypes удалены - типы организаций теперь не используются (организации в KLS)

  const { data: organizations = [], isLoading: isLoadingOrganizations } = useQuery<Organization[]>({
    queryKey: ["organizations"],
    queryFn: () => organizationsApi.list({ limit: 1000 }),
    enabled: !!user && hasPermission("admin.access"),
  });

  const { data: roles = [] } = useQuery<Role[]>({
    queryKey: ["roles"],
    queryFn: () => rolesApi.list(),
    enabled: !!user && hasPermission("admin.access"),
  });

  // Mutations for object types
  const createObjectTypeMutation = useMutation({
    mutationFn: (data: ObjectTypeCreate) => objectTypesApi.create(data),
    onSuccess: () => {
      toast({
        title: "Тип объекта создан",
        description: "Операция выполнена успешно",
      });
      queryClient.invalidateQueries({ queryKey: ["object-types"] });
      setIsDialogOpen(false);
      setEditingItem(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось создать тип объекта",
        variant: "destructive",
      });
    },
  });

  const updateObjectTypeMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: ObjectTypeUpdate }) => 
      objectTypesApi.update(id, data),
    onSuccess: () => {
      toast({
        title: "Тип объекта обновлен",
        description: "Операция выполнена успешно",
      });
      queryClient.invalidateQueries({ queryKey: ["object-types"] });
      setIsDialogOpen(false);
      setEditingItem(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось обновить тип объекта",
        variant: "destructive",
      });
    },
  });

  const deleteObjectTypeMutation = useMutation({
    mutationFn: (id: number) => objectTypesApi.delete(id),
    onSuccess: () => {
      toast({
        title: "Тип объекта удален",
        description: "Тип объекта успешно удален из системы",
      });
      queryClient.invalidateQueries({ queryKey: ["object-types"] });
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось удалить тип объекта",
        variant: "destructive",
      });
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    },
  });

  // Mutations for org types - УДАЛЕНЫ (типы организаций теперь не используются)

  // Mutations for organizations - УДАЛЕНЫ (организации управляются через KLS API)

  // Handlers for references section
  const handleEditObjectType = (type: ObjectType) => {
    setEditingItem({ ...type });
    setIsDialogOpen(true);
  };

  const handleDeleteObjectType = (id: number, name: string) => {
    setConfirmDialog({
      isOpen: true,
      title: "Удаление типа объекта",
      description: `Вы уверены, что хотите удалить тип объекта "${name}"?\n\n⚠️ ВАЖНО! При удалении типа объекта будут удалены:\n• Все объекты данного типа\n• Все связанные геометрии\n• Все файлы и документы\n• Все дочерние типы объектов\n\nЭто действие необратимо!`,
      onConfirm: () => {
        deleteObjectTypeMutation.mutate(id);
      }
    });
  };

  // handleEditOrgType, handleDeleteOrgType, handleEditOrganization, handleDeleteOrganization удалены
  // - типы организаций теперь не используются
  // - организации управляются через KLS

  // Функция для создания иерархического списка типов объектов
  const buildTypeHierarchy = (items: ObjectType[], parentId: number | null = null, level = 0): JSX.Element[] => {
    const filteredItems = items.filter(item => item.parent_id === parentId);
    
    return filteredItems.map((item, index) => {
      const isLast = index === filteredItems.length - 1;
      const hasChildren = items.some(child => child.parent_id === item.id);
      const childrenCount = items.filter(child => child.parent_id === item.id).length;
      
      return [
        <TableRow key={item.id} className={level > 0 ? "bg-muted/40" : ""}>
          <TableCell className="font-medium">
            <div className="flex items-center gap-2">
              <div className="flex items-center" style={{ marginLeft: `${level * 24}px` }}>
                {level > 0 && (
                  <div className="flex items-center mr-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <div className={`w-3 h-3 border-l-2 border-b-2 border-border ${isLast ? '' : 'border-b-0'}`}></div>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  {level === 0 ? (
                    <div className="w-6 h-6 bg-blue-100 dark:bg-blue-950/60 rounded flex items-center justify-center">
                      <LayersIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 bg-green-100 dark:bg-green-950/60 rounded flex items-center justify-center">
                      <Building2Icon className="h-3 w-3 text-green-600 dark:text-green-400" />
                    </div>
                  )}
                  
                  <div className="flex flex-col">
                    <span className={level === 0 ? "font-semibold text-foreground" : "text-foreground/90"}>
                      {item.name}
                    </span>
                    {hasChildren && (
                      <span className="text-xs text-muted-foreground">
                        {childrenCount} подтип{childrenCount === 1 ? '' : childrenCount < 5 ? 'а' : 'ов'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TableCell>
          <TableCell>
            <div className="flex flex-col">
              <span className="text-sm text-foreground">{item.description || '-'}</span>
              {item.parent_id && (
                <span className="text-xs text-muted-foreground mt-1">
                  Родитель: {items.find(parent => parent.id === item.parent_id)?.name || 'Не найден'}
                </span>
              )}
            </div>
          </TableCell>
          <TableCell className="text-right">
            <div className="flex items-center justify-end gap-1">
              <Button variant="ghost" size="icon" onClick={() => handleEditObjectType(item)} className="h-8 w-8">
                <PencilIcon className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => handleDeleteObjectType(item.id, item.name)} className="h-8 w-8">
                <TrashIcon className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          </TableCell>
        </TableRow>,
        ...buildTypeHierarchy(items, item.id, level + 1)
      ];
    }).flat();
  };

  // buildOrgTypeHierarchy удалена - типы организаций теперь не используются

  // Функция для создания иерархического списка организаций (из KLS)
  const buildOrganizationHierarchy = (items: Organization[], parentId: number | null = null, level = 0): JSX.Element[] => {
    const filteredItems = items.filter(item => item.parent_id === parentId);
    
    return filteredItems.map((item, index) => {
      const isLast = index === filteredItems.length - 1;
      const hasChildren = items.some(child => child.parent_id === item.id);
      const childrenCount = items.filter(child => child.parent_id === item.id).length;
      // orgType удален - организации теперь в KLS без типов
      
      return [
        <TableRow key={item.id} className={level > 0 ? "bg-muted/40" : ""}>
          <TableCell className="font-medium">
            <div className="flex items-center gap-2">
              <div className="flex items-center" style={{ marginLeft: `${level * 24}px` }}>
                {level > 0 && (
                  <div className="flex items-center mr-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <div className={`w-3 h-3 border-l-2 border-b-2 border-purple-400/50 dark:border-purple-500/40 ${isLast ? '' : 'border-b-0'}`}></div>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  {level === 0 ? (
                    <div className="w-6 h-6 bg-purple-100 dark:bg-purple-950/60 rounded flex items-center justify-center">
                      <UsersIcon className="h-4 w-4 text-purple-600 dark:text-purple-300" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 bg-indigo-100 dark:bg-indigo-950/60 rounded flex items-center justify-center">
                      <UsersIcon className="h-3 w-3 text-indigo-600 dark:text-indigo-300" />
                    </div>
                  )}
                  
                  <div className="flex flex-col">
                    <span className={level === 0 ? "font-semibold text-foreground" : "text-foreground/90"}>
                      {item.name}
                    </span>
                    {hasChildren && (
                      <span className="text-xs text-muted-foreground">
                        {childrenCount} дочерн{childrenCount === 1 ? 'ий орган' : childrenCount < 5 ? 'их органа' : 'их органов'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TableCell>
          <TableCell>
            <div className="flex flex-col">
              <span className="text-sm text-foreground">{item.description || '-'}</span>
              {item.parent_id && (
                <span className="text-xs text-muted-foreground mt-1">
                  Родитель: {items.find(parent => parent.id === item.parent_id)?.name || 'Не найден'}
                </span>
              )}
            </div>
          </TableCell>
          <TableCell className="text-right">
            {/* Кнопки редактирования/удаления убраны - организации управляются через KLS */}
            <span className="text-xs text-muted-foreground">KLS</span>
          </TableCell>
        </TableRow>,
        ...buildOrganizationHierarchy(items, item.id, level + 1)
      ];
    }).flat();
  };

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center flex-col">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary mb-4"></div>
        <div className="text-lg font-medium">Проверка прав доступа...</div>
        <div className="text-sm text-muted-foreground mt-2">Если вы не администратор, вы будете перенаправлены</div>
      </div>
    );
  }

  // Проверка прав доступа к админ-панели
  if (!user || !hasPermission("admin.access")) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      {/* Фиксированная шапка — на всю ширину окна с адаптивными полями */}
      <div className="flex-shrink-0 bg-card border-b border-border px-4 sm:px-6 lg:px-8 xl:px-10 py-4 w-full min-w-0">
        <Link to="/">
          <Button variant="ghost" className="mb-2">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Вернуться на главную
          </Button>
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Панель администратора</h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Управление справочниками и настройками системы
        </p>
      </div>

      {/* Основная область с навигацией и контентом */}
      <div className="flex-1 overflow-hidden min-h-0 min-w-0">
        <div className="w-full max-w-none h-full min-w-0 box-border px-4 sm:px-6 lg:px-8 xl:px-10 py-4 sm:py-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6 h-full min-w-0">
            <div className="col-span-1 md:col-span-3 flex flex-col space-y-4 sm:space-y-6 overflow-y-auto min-w-0 md:max-h-full pr-0 md:pr-1">
              <Card className="flex flex-col flex-shrink-0">
                <CardHeader className="pb-3 flex-shrink-0">
                <CardTitle>Навигация</CardTitle>
                <CardDescription>Разделы административной панели</CardDescription>
              </CardHeader>
                <CardContent className="space-y-1 p-0 flex-1 overflow-y-auto">
                <Button 
                  variant={activeTab === "references" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("references")}
                >
                  <DatabaseIcon className="mr-2 h-4 w-4" />
                  Справочники
                </Button>
                <Button 
                  variant={activeTab === "settings" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("settings")}
                >
                  <Settings2Icon className="mr-2 h-4 w-4" />
                  Настройки системы
                </Button>
                <Button 
                  variant={activeTab === "database" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("database")}
                >
                  <DatabaseIcon className="mr-2 h-4 w-4" />
                  База данных
                </Button>
                <Button 
                  variant={activeTab === "users" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("users")}
                >
                  <UsersIcon className="mr-2 h-4 w-4" />
                  Пользователи и права
                </Button>
                <Button 
                  variant={activeTab === "quotas" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("quotas")}
                >
                  <Settings2Icon className="mr-2 h-4 w-4" />
                  Управление квотами
                </Button>
                <Button 
                  variant={activeTab === "import-export" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("import-export")}
                >
                  <ArrowRightLeftIcon className="mr-2 h-4 w-4" />
                  Импорт/Экспорт
                </Button>
                <Button
                  variant={activeTab === "go-import" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("go-import")}
                >
                  <MapIcon className="mr-2 h-4 w-4" />
                  Импорт ГО
                </Button>
                <Button 
                  variant={activeTab === "integrations" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("integrations")}
                >
                  <GlobeIcon className="mr-2 h-4 w-4" />
                  Интеграции
                </Button>
                <Button 
                  variant={activeTab === "ai-agents" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("ai-agents")}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  ИИ-агенты
                </Button>
                <Button 
                  variant={activeTab === "data-access" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("data-access")}
                >
                  <ShieldIcon className="mr-2 h-4 w-4" />
                  Управление доступом
                </Button>
                <Button 
                  variant={activeTab === "storage" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("storage")}
                >
                  <FolderIcon className="mr-2 h-4 w-4" />
                  Хранилище данных
                </Button>
                <Button 
                  variant={activeTab === "api" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("api")}
                >
                  <Settings2Icon className="mr-2 h-4 w-4" />
                  Настройка API
                </Button>
                <Button 
                  variant={activeTab === "card-templates" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("card-templates")}
                >
                  <LayoutTemplate className="mr-2 h-4 w-4" />
                  Шаблоны карточек
                </Button>
                <Button 
                  variant={activeTab === "reports" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("reports")}
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Конструктор отчётов
                </Button>
                {hasPermission("notifications.manage") && (
                  <Button
                    variant={activeTab === "notifications" ? "default" : "ghost"}
                    className="w-full justify-start pl-3 rounded-none"
                    onClick={() => setActiveTab("notifications")}
                  >
                    <Bell className="mr-2 h-4 w-4" />
                    Уведомления
                  </Button>
                )}
                {hasPermission("feedback.manage") && (
                  <Button
                    variant={activeTab === "feedback" ? "default" : "ghost"}
                    className="w-full justify-start pl-3 rounded-none"
                    onClick={() => setActiveTab("feedback")}
                  >
                    <Bug className="mr-2 h-4 w-4" />
                    Обратная связь
                  </Button>
                )}
                {hasPermission("onboarding.manage") && (
                  <Button
                    variant={activeTab === "onboarding" ? "default" : "ghost"}
                    className="w-full justify-start pl-3 rounded-none"
                    onClick={() => setActiveTab("onboarding")}
                  >
                    <Compass className="mr-2 h-4 w-4" />
                    Онбординг
                  </Button>
                )}
                {hasPermission("chat.manage_gifs") && (
                  <Button
                    variant={activeTab === "chat-gifs" ? "default" : "ghost"}
                    className="w-full justify-start pl-3 rounded-none"
                    onClick={() => setActiveTab("chat-gifs")}
                  >
                    <ImageIcon className="mr-2 h-4 w-4" />
                    Чат / GIF
                  </Button>
                )}
                {hasPermission("reference_layers.manage") && (
                  <Button
                    variant={activeTab === "reference-layers" ? "default" : "ghost"}
                    className="w-full justify-start pl-3 rounded-none"
                    onClick={() => setActiveTab("reference-layers")}
                  >
                    <LayersIcon className="mr-2 h-4 w-4" />
                    Справочные слои
                  </Button>
                )}
                <Button 
                  variant={activeTab === "mcp" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("mcp")}
                >
                  <LayersIcon className="mr-2 h-4 w-4" />
                  Model Context Protocol
                </Button>
                <Button 
                  variant={activeTab === "bot-settings" ? "default" : "ghost"}
                  className="w-full justify-start pl-3 rounded-none"
                  onClick={() => setActiveTab("bot-settings")}
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Настройки бота
                </Button>
              </CardContent>
            </Card>

              <Card className="flex flex-col flex-shrink-0 max-h-[calc(100vh-500px)] min-h-0">
                <CardHeader className="pb-2 pt-3 px-3 flex-shrink-0">
                  <CardTitle className="text-sm font-semibold">Статистика</CardTitle>
                  <CardDescription className="text-xs text-muted-foreground">Информация о системе</CardDescription>
              </CardHeader>
                <CardContent className="px-3 pb-3 flex-1 overflow-y-auto min-h-0">
                {isLoadingStats || isLoadingObjectTypes || isLoadingOrganizations ? (
                  <div className="space-y-1.5">
                    <div className="h-3 w-full bg-muted rounded animate-pulse"></div>
                    <div className="h-3 w-full bg-muted rounded animate-pulse"></div>
                    <div className="h-3 w-full bg-muted rounded animate-pulse"></div>
                    <div className="h-3 w-full bg-muted rounded animate-pulse"></div>
                    <div className="h-3 w-full bg-muted rounded animate-pulse"></div>
                    <div className="h-3 w-full bg-muted rounded animate-pulse"></div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <DatabaseIcon className="h-3 w-3 text-blue-500 flex-shrink-0" />
                        <span className="text-xs text-muted-foreground truncate">Типы объектов</span>
                    </div>
                      <span className="text-xs font-semibold text-foreground ml-2">{objectTypes?.length || 0}</span>
                    </div>
                    {/* Типы организаций удалены - организации теперь в KLS */}
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Building2Icon className="h-3 w-3 text-green-500 flex-shrink-0" />
                        <span className="text-xs text-muted-foreground truncate">Органы управления</span>
                    </div>
                      <span className="text-xs font-semibold text-foreground ml-2">{organizations?.length || 0}</span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <LayersIcon className="h-3 w-3 text-orange-500 flex-shrink-0" />
                        <span className="text-xs text-muted-foreground truncate">Объекты</span>
                      </div>
                      <span className="text-xs font-semibold text-foreground ml-2">{stats?.totalObjects || 0}</span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <GlobeIcon className="h-3 w-3 text-cyan-500 flex-shrink-0" />
                        <span className="text-xs text-muted-foreground truncate">Геометрии</span>
                      </div>
                      <span className="text-xs font-semibold text-foreground ml-2">{stats?.totalGeometries || 0}</span>
                    </div>
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <UsersIcon className="h-3 w-3 text-indigo-500 flex-shrink-0" />
                        <span className="text-xs text-muted-foreground truncate">Пользователи</span>
                      </div>
                      <span className="text-xs font-semibold text-foreground ml-2">{stats?.totalUsers || 0}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

            <div className="col-span-1 md:col-span-9 flex flex-col overflow-hidden min-h-0 min-w-0">
              <Card className="flex flex-col h-full min-h-0 min-w-0 shadow-sm">
                <CardHeader className="pb-3 flex-shrink-0">
                <div className="flex justify-between items-center">
                  <CardTitle>
                      {activeTab === "references" && "Управление справочниками"}
                    {activeTab === "settings" && "Настройки системы"}
                    {activeTab === "database" && "База данных"}
                    {activeTab === "users" && "Пользователи и права"}
                    {activeTab === "quotas" && "Управление квотами"}
                    {activeTab === "integrations" && "Управление интеграциями с сервисами"}
                    {activeTab === "ai-agents" && "ИИ-агенты и LLM-провайдеры"}
                    {activeTab === "data-access" && "Управление доступом к данным"}
                    {activeTab === "api" && "Управление настройками API"}
                    {activeTab === "mcp" && "Управление Model Context Protocol (MCP)"}
                    {activeTab === "bot-settings" && "Настройки Telegram бота"}
                    {activeTab === "storage" && "Управление хранилищем данных"}
                    {activeTab === "card-templates" && "Шаблоны карточек объектов"}
                    {activeTab === "reports" && "Конструктор отчётов"}
                    {activeTab === "notifications" && "Уведомления"}
                    {activeTab === "feedback" && "Обратная связь"}
                    {activeTab === "chat-gifs" && "Чат / GIF"}
                    {activeTab === "onboarding" && "Онбординг"}
                    {activeTab === "go-import" && "Импорт / seed модуля ГО"}
                    {activeTab === "reference-layers" && "Справочные слои GeoServer"}
                  </CardTitle>
                </div>
              </CardHeader>
                <CardContent className="p-0 flex-1 overflow-y-auto overflow-x-auto min-w-0">
                {activeTab === "references" && (
                  <div className="p-4">
                    <Tabs value={activeReferenceTab} onValueChange={setActiveReferenceTab}>
                      {/* Tabs с переносом на несколько строк для лучшей читаемости */}
                      <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 gap-2 h-auto p-1">
                        <TabsTrigger value="object-types" className="text-sm py-2.5">
                          Типы объектов
                        </TabsTrigger>
                        {/* TabsTrigger для org-types удален - типы организаций теперь не используются */}
                        <TabsTrigger value="organizations" className="text-sm py-2.5">Органы управления</TabsTrigger>
                        <TabsTrigger value="qual-types" className="text-sm py-2.5">Типы квалификаторов</TabsTrigger>
                        <TabsTrigger value="quals" className="text-sm py-2.5">Квалификаторы</TabsTrigger>
                        <TabsTrigger value="kls" className="text-sm py-2.5">Разделы классификаторов</TabsTrigger>
                        <TabsTrigger value="geometry-semantics" className="text-sm py-2.5">
                          Семантика геометрий
                        </TabsTrigger>
                        <TabsTrigger value="external-sources" className="text-sm py-2.5">Внешние источники</TabsTrigger>
                        <TabsTrigger value="sync-logs" className="text-sm py-2.5">Логи синхронизации</TabsTrigger>
                      </TabsList>

                      <TabsContent value="object-types" className="mt-4 space-y-4">
                        <ReferencesSectionHeader
                          title="Типы объектов"
                          description={
                            <>
                              Иерархия типов задаётся здесь. Для назначения объекта в формах используется квалификатор
                              KLS <span className="font-mono">qual_id = 354</span>. Разделы этого справочника
                              редактируются на вкладках «Квалификаторы» и «Разделы классификаторов».
                            </>
                          }
                          actions={
                            <Button
                              onClick={() => {
                                setEditingItem({ name: "", description: "", parent_id: null });
                                setIsDialogOpen(true);
                              }}
                            >
                              <PlusIcon className="mr-2 h-4 w-4" />
                              Добавить тип
                            </Button>
                          }
                        />
                        <div className="border rounded-md overflow-hidden">
                          <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
                          <Table>
                              <TableHeader className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm shadow-sm border-b border-border">
                              <TableRow>
                                <TableHead>Название и иерархия</TableHead>
                                <TableHead>Описание</TableHead>
                                <TableHead className="text-right">Действия</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {isLoadingObjectTypes ? (
                                <TableRow>
                                  <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6">
                                    Загрузка...
                                  </TableCell>
                                </TableRow>
                              ) : objectTypes.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-6">
                                    Типы объектов отсутствуют
                                  </TableCell>
                                </TableRow>
                              ) : (
                                buildTypeHierarchy(objectTypes)
                              )}
                            </TableBody>
                          </Table>
                          </div>
                        </div>
                      </TabsContent>

                      {/* TabsContent для org-types удален - типы организаций теперь не используются */}

                      <TabsContent value="organizations" className="mt-4 space-y-4">
                        <ReferencesSectionHeader
                          title="Органы управления"
                          description={
                            <>
                              Органы управления хранятся в KLS (<span className="font-mono">qual_id = 336</span>). Для
                              управления используйте вкладку «Квалификаторы».
                            </>
                          }
                        />
                        <div className="border rounded-md overflow-hidden">
                          <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
                          <Table>
                              <TableHeader className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm shadow-sm border-b border-border">
                              <TableRow>
                                <TableHead>Название и иерархия</TableHead>
                                <TableHead>Тип</TableHead>
                                <TableHead>Описание</TableHead>
                                <TableHead className="text-right">Действия</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {isLoadingOrganizations ? (
                                <TableRow>
                                  <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-6">
                                    Загрузка...
                                  </TableCell>
                                </TableRow>
                              ) : organizations.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-6">
                                    Органы управления отсутствуют
                                  </TableCell>
                                </TableRow>
                              ) : (
                                buildOrganizationHierarchy(organizations)
                              )}
                            </TableBody>
                          </Table>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="qual-types" className="mt-4 space-y-4">
                        <QualTypeManagement />
                      </TabsContent>

                      <TabsContent value="quals" className="mt-4 space-y-4">
                        <QualManagement />
                      </TabsContent>

                      <TabsContent value="kls" className="mt-4 space-y-4">
                        <KlsManagement />
                      </TabsContent>

                      <TabsContent value="geometry-semantics" className="mt-4 space-y-4">
                        <ReferencesSectionHeader
                          title="Семантика геометрий"
                          description={
                            <>
                              Квалификатор <span className="font-mono">qual_id = 345</span>. Разделы задают семантику
                              геометрий при рисовании и в правилах шаблонов карточек. Полный справочник KLS доступен на
                              вкладке «Разделы классификаторов».
                            </>
                          }
                        />
                        <KlsManagement initialQualId={345} hideTitle />
                      </TabsContent>

                      <TabsContent value="external-sources" className="mt-4 space-y-4">
                        <ExternalSourcesManagement />
                      </TabsContent>

                      <TabsContent value="sync-logs" className="mt-4 space-y-4">
                        <SyncLogsView />
                      </TabsContent>
                    </Tabs>
                  </div>
                )}
                
                {activeTab === "users" && (
                  <div className="p-4">
                    <UserManagement />
                  </div>
                )}
                {activeTab === "quotas" && (
                  <div className="p-4">
                    <QuotaManagement />
                  </div>
                )}
                {activeTab === "data-access" && (
                  <div className="p-4">
                    <DataAccessManagement />
                  </div>
                )}
                
                {activeTab === "bot-settings" && (
                  <div className="p-4">
                    <BotSettingsManagement />
                  </div>
                )}
                
                {activeTab === "settings" && (
                  <div className="p-6">
                    {systemSettingsError ? (
                      <Alert variant="destructive" className="mb-4">
                        <AlertTitle>Не удалось загрузить настройки</AlertTitle>
                        <AlertDescription>
                          {(systemSettingsError as Error).message || "Проверьте сеть и права администратора."}
                        </AlertDescription>
                      </Alert>
                    ) : null}
                    {systemSettingsLoading ? (
                      <div className="text-sm text-muted-foreground py-8">Загрузка настроек…</div>
                    ) : (
                      <>
                    <h3 className="text-lg font-medium mb-4">Общие настройки</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Отображаемое имя и параметры карты по умолчанию хранятся в БД (таблица{" "}
                      <span className="font-mono text-xs">api.admin_system_settings</span>) и могут быть использованы клиентом при необходимости.
                    </p>
                    <div className="space-y-6">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="app-name">Название приложения</Label>
                            <Input
                              id="app-name"
                              value={systemForm.app_display_name}
                              onChange={(e) =>
                                setSystemForm((s) => ({ ...s, app_display_name: e.target.value }))
                              }
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="default-zoom">Масштаб карты по умолчанию</Label>
                            <Input
                              id="default-zoom"
                              type="number"
                              min={1}
                              max={22}
                              value={systemForm.map_default_zoom}
                              onChange={(e) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  map_default_zoom: Number(e.target.value) || 1,
                                }))
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="default-center">Центр карты по умолчанию (WGS84)</Label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                              id="default-center-lat"
                              placeholder="Широта"
                              type="number"
                              step="any"
                              value={systemForm.map_default_center_lat}
                              onChange={(e) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  map_default_center_lat: parseFloat(e.target.value) || 0,
                                }))
                              }
                            />
                            <Input
                              id="default-center-lng"
                              placeholder="Долгота"
                              type="number"
                              step="any"
                              value={systemForm.map_default_center_lng}
                              onChange={(e) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  map_default_center_lng: parseFloat(e.target.value) || 0,
                                }))
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <h3 className="text-lg font-medium mb-4">Настройки пользователей</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Политика эндпоинта <span className="font-mono text-xs">POST /auth/register</span>
                          : роль и организация из формы саморегистрации не принимаются — только значения ниже.
                        </p>
                        <div className="space-y-4">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="allow-registration"
                              checked={systemForm.allow_registration}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  allow_registration: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="allow-registration">Разрешить регистрацию новых пользователей</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="auto-activate-users"
                              checked={systemForm.auto_activate_new_users}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  auto_activate_new_users: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="auto-activate-users">Автоматически активировать новых пользователей</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="require-email-verification"
                              checked={systemForm.require_email_verification}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  require_email_verification: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="require-email-verification">
                              Требовать подтверждение email (учётная запись неактивна до подтверждения; рассылка в MVP не подключена)
                            </Label>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="default-user-role">Роль по умолчанию для новых пользователей</Label>
                            <Select
                              value={systemForm.default_new_user_role_id}
                              onValueChange={(value) =>
                                setSystemForm((s) => ({ ...s, default_new_user_role_id: value }))
                              }
                            >
                              <SelectTrigger id="default-user-role">
                                <SelectValue placeholder="Выберите роль" />
                              </SelectTrigger>
                              <SelectContent className="z-[250]">
                                <SelectItem value="none">Без роли</SelectItem>
                                {roles.map((role) => (
                                  <SelectItem key={role.id} value={role.id.toString()}>
                                    {role.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="default-user-organization">Орган управления по умолчанию</Label>
                            <Select
                              value={systemForm.default_new_user_organization_id}
                              onValueChange={(value) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  default_new_user_organization_id: value,
                                }))
                              }
                            >
                              <SelectTrigger id="default-user-organization">
                                <SelectValue placeholder="Выберите орган управления" />
                              </SelectTrigger>
                              <SelectContent className="z-[250]">
                                <SelectItem value="none">Без органа управления</SelectItem>
                                {organizations.map((org) => (
                                  <SelectItem key={org.id} value={org.id.toString()}>
                                    {org.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <h3 className="text-lg font-medium mb-4">Модули</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Глобальное включение модулей платформы. Выключенный модуль скрыт в UI и отклоняется API
                          (403). Выключение не удаляет данные модуля.
                        </p>
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="module-oi"
                              checked={systemForm.module_oi_enabled}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  module_oi_enabled: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="module-oi">Объекты (ОИ)</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="module-rd"
                              checked={systemForm.module_rd_enabled}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  module_rd_enabled: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="module-rd">Согласования</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="module-go"
                              checked={systemForm.module_go_enabled}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  module_go_enabled: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="module-go">Географический обзор (ГО)</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="module-udgspz"
                              checked={systemForm.module_udgspz_enabled}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  module_udgspz_enabled: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="module-udgspz">Геостратегическое зонирование (УДГСПЗ)</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="module-visualizer"
                              checked={systemForm.module_visualizer_enabled}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  module_visualizer_enabled: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="module-visualizer">Визуализатор</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="module-tactical"
                              checked={systemForm.module_tactical_enabled}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  module_tactical_enabled: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="module-tactical">Тактическая обстановка</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="module-work-items"
                              checked={systemForm.module_work_items_enabled}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  module_work_items_enabled: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="module-work-items">
                              Задачи и инциденты (меню, API и слой Telegram на карте)
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="module-feedback"
                              checked={systemForm.module_feedback_enabled}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  module_feedback_enabled: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="module-feedback">
                              Обратная связь (отчёты об ошибках)
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="module-onboarding"
                              checked={systemForm.module_onboarding_enabled}
                              onCheckedChange={(checked) =>
                                setSystemForm((s) => ({
                                  ...s,
                                  module_onboarding_enabled: checked === true,
                                }))
                              }
                            />
                            <Label htmlFor="module-onboarding">
                              Онбординг (guided tours)
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="module-chat"
                              checked={systemForm.module_chat_enabled}
                              onCheckedChange={(checked) => {
                                if (checked === true) {
                                  setSystemForm((s) => ({ ...s, module_chat_enabled: true }));
                                  return;
                                }
                                setChatDisableConfirmOpen(true);
                              }}
                            />
                            <Label htmlFor="module-chat">Чат (E2EE-мессенджер)</Label>
                          </div>
                        </div>
                      </div>

                      <AlertDialog open={chatDisableConfirmOpen} onOpenChange={setChatDisableConfirmOpen}>
                        <AlertDialogContent className="z-[230]">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Выключить модуль чата?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Модуль будет скрыт. Данные сохранятся. Пользователи потеряют доступ к чату
                              до повторного включения. API и WebSocket чата начнут отвечать отказом после
                              сохранения настроек.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Отмена</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() => {
                                setSystemForm((s) => ({ ...s, module_chat_enabled: false }));
                                setChatDisableConfirmOpen(false);
                              }}
                            >
                              Выключить
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>

                      <div className="flex justify-end mt-6">
                        <Button
                          onClick={() => saveSettingsMutation.mutate()}
                          disabled={saveSettingsMutation.isPending || systemSettingsLoading}
                        >
                          {saveSettingsMutation.isPending ? "Сохранение..." : "Сохранить настройки"}
                        </Button>
                      </div>

                      <div className="mt-10 border-t pt-6">
                        <h3 className="text-lg font-medium mb-2">Безопасность (2FA / PIN)</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Глобальные флаги модуля security. Требуется право security.manage.
                        </p>
                        <SecurityFeatureFlagsPanel />
                      </div>
                    </div>
                      </>
                    )}
                  </div>
                )}

                {activeTab === "database" && (
                  <div className="p-6">
                    <h3 className="text-lg font-medium mb-4 text-foreground">Управление базой данных</h3>
                    <Alert className="mb-6">
                      <AlertTitle>Функции не подключены к API</AlertTitle>
                      <AlertDescription>
                        Резервное копирование, восстановление и обслуживание через эту панель не реализованы во избежание
                        ложных уведомлений об успехе. В продакшене используйте{" "}
                        <span className="font-mono text-xs">pg_dump</span>, <span className="font-mono text-xs">pg_restore</span>{" "}
                        и задачи по расписанию на сервере СУБД.
                      </AlertDescription>
                    </Alert>
                    <div className="space-y-6 opacity-60 pointer-events-none">
                      <div className="p-4 bg-muted/50 border border-border rounded-lg">
                        <h4 className="font-medium mb-2 text-foreground">Резервное копирование</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          Создание резервной копии базы данных позволяет сохранить все данные системы и восстановить их при необходимости.
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <Button type="button" disabled>Создать резервную копию</Button>
                          <Button type="button" variant="outline" disabled>
                            Восстановить из копии
                          </Button>
                        </div>
                      </div>
                      <div className="p-4 bg-muted/50 border border-border rounded-lg">
                        <h4 className="font-medium mb-2 text-foreground">Обслуживание базы данных</h4>
                        <p className="text-sm text-muted-foreground mb-4">
                          Периодическое обслуживание базы данных помогает поддерживать высокую производительность системы.
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <Button type="button" disabled>Оптимизировать базу данных</Button>
                          <Button type="button" variant="outline" disabled>Проверить целостность</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeTab === "import-export" && (
                  <div className="p-6">
                    <DataExchangeTabs />
                  </div>
                )}

                {activeTab === "go-import" && <GoAdminImportConsole />}
                
                {activeTab === "integrations" && <ServiceIntegrationManagement />}
                {activeTab === "ai-agents" && <AiAgentsAdminPanel />}
                
                {activeTab === "storage" && (
                  <div className="p-4">
                    <StorageManagement />
                  </div>
                )}

                {activeTab === "api" && (
                  <div className="p-4">
                    <ApiManagement />
                  </div>
                )}

                {activeTab === "card-templates" && (
                  <div className="p-4">
                    <CardTemplatesHub />
                  </div>
                )}

                {activeTab === "reports" && (
                  <div className="p-4">
                    <ReportsManager />
                  </div>
                )}

                {activeTab === "notifications" && hasPermission("notifications.manage") && (
                  <div className="p-4">
                    <NotificationsAdmin />
                  </div>
                )}

                {activeTab === "feedback" && hasPermission("feedback.manage") && (
                  <div className="p-4">
                    <FeedbackAdmin />
                  </div>
                )}

                {activeTab === "onboarding" && hasPermission("onboarding.manage") && (
                  <div className="p-4">
                    <OnboardingAdminPanel />
                  </div>
                )}

                {activeTab === "chat-gifs" && hasPermission("chat.manage_gifs") && (
                  <div className="p-4">
                    <ChatGifsAdminPanel />
                  </div>
                )}

                {activeTab === "reference-layers" && hasPermission("reference_layers.manage") && (
                  <div className="p-4">
                    <ReferenceLayerManagement />
                  </div>
                )}

                {activeTab === "mcp" && (
                  <div className="p-6 space-y-6">
                    <div className="mb-6">
                      <h3 className="text-lg font-medium mb-2">Model Context Protocol (MCP)</h3>
                      <p className="text-sm text-muted-foreground">
                        Открытый протокол для бесшовной интеграции между приложениями LLM и внешними источниками данных и инструментами.
                      </p>
                    </div>

                    <Tabs value={mcpActiveTab} onValueChange={setMcpActiveTab} className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="server">MCP-сервер</TabsTrigger>
                        <TabsTrigger value="client">MCP-клиент</TabsTrigger>
                      </TabsList>

                      <TabsContent value="server" className="space-y-6 mt-6">
                        <div className="grid gap-6">
                          <Card>
                            <CardHeader>
                              <CardTitle>Инструменты MCP</CardTitle>
                              <CardDescription>
                                Настройка и управление инструментами для работы с Model Context Protocol
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              <div className="flex items-center justify-between p-4 border rounded-lg">
                                <div>
                                  <h4 className="font-medium">Геопространственные данные</h4>
                                  <p className="text-sm text-muted-foreground">
                                    Доступ к кадастровым данным и геометриям объектов
                                  </p>
                                </div>
                                <Button variant="outline" onClick={() => setIsMcpGeoDialogOpen(true)}>
                                  Настроить
                                </Button>
                              </div>

                              <div className="flex items-center justify-between p-4 border rounded-lg">
                                <div>
                                  <h4 className="font-medium">Управление объектами</h4>
                                  <p className="text-sm text-muted-foreground">
                                    Создание, редактирование и удаление объектов
                                  </p>
                                </div>
                                <Button variant="outline" onClick={() => setIsMcpObjectsDialogOpen(true)}>
                                  Настроить
                                </Button>
                              </div>

                              <div className="flex items-center justify-between p-4 border rounded-lg">
                                <div>
                                  <h4 className="font-medium">Аналитика и отчеты</h4>
                                  <p className="text-sm text-muted-foreground">
                                    Генерация отчетов и статистической информации
                                  </p>
                                </div>
                                <Button variant="outline" onClick={() => setIsMcpAnalyticsDialogOpen(true)}>
                                  Настроить
                                </Button>
                              </div>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardHeader>
                              <CardTitle>Настройки подключения</CardTitle>
                              <CardDescription>
                                Конфигурация протокола MCP и параметров подключения
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              <div className="space-y-2">
                                <Label htmlFor="mcp-endpoint">Endpoint MCP сервера</Label>
                                <Input 
                                  id="mcp-endpoint" 
                                  placeholder="ws://localhost:3001/mcp"
                                  defaultValue="ws://localhost:3001/mcp"
                                />
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="mcp-timeout">Таймаут соединения (секунды)</Label>
                                <Input 
                                  id="mcp-timeout" 
                                  type="number"
                                  placeholder="30"
                                  defaultValue="30"
                                />
                              </div>

                              <div className="flex items-center space-x-2">
                                <Checkbox id="mcp-auto-reconnect" defaultChecked />
                                <Label htmlFor="mcp-auto-reconnect">
                                  Автоматическое переподключение
                                </Label>
                              </div>

                              <div className="flex items-center space-x-2">
                                <Checkbox id="mcp-logging" defaultChecked />
                                <Label htmlFor="mcp-logging">
                                  Включить логирование MCP запросов
                                </Label>
                              </div>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardHeader>
                              <CardTitle>Статус подключения</CardTitle>
                              <CardDescription>
                                Текущее состояние MCP сервера и активных подключений
                              </CardDescription>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium">Статус сервера:</span>
                                  <div className="flex items-center space-x-2">
                                    <div className={`w-2 h-2 rounded-full ${
                                      mcpServerStatus === 'running' ? 'bg-green-500' :
                                      mcpServerStatus === 'starting' ? 'bg-yellow-500' :
                                      mcpServerStatus === 'error' ? 'bg-red-500' :
                                      mcpServerStatus === null ? 'bg-muted-foreground/40 animate-pulse' :
                                      'bg-muted-foreground/60'
                                    }`}></div>
                                    <span className="text-sm text-muted-foreground">
                                      {mcpServerStatus === null ? 'Загрузка...' :
                                       mcpServerStatus === 'running' ? 'Работает' :
                                       mcpServerStatus === 'starting' ? 'Запускается...' :
                                       mcpServerStatus === 'error' ? 'Ошибка' :
                                       'Остановлен'}
                                    </span>
                                  </div>
                                </div>
                                
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium">Активные подключения:</span>
                                  <span className="text-sm text-muted-foreground">{mcpActiveConnections}</span>
                                </div>
                                
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium">Доступные инструменты:</span>
                                  <span className="text-sm text-muted-foreground">{mcpStatus?.availableTools || 3}</span>
                                </div>

                                <div className="pt-4 border-t">
                                  {mcpServerStatus === 'running' ? (
                                    <Button 
                                      variant="destructive" 
                                      className="w-full"
                                      onClick={handleStopMcpServer}
                                      disabled={mcpStartLoading}
                                    >
                                      {mcpStartLoading ? 'Останавливается...' : 'Остановить MCP сервер'}
                                    </Button>
                                  ) : (
                                    <Button 
                                      className="w-full"
                                      onClick={handleStartMcpServer}
                                      disabled={mcpStartLoading || mcpServerStatus === 'starting' || mcpServerStatus === null}
                                    >
                                      {mcpStartLoading || mcpServerStatus === 'starting' ? 'Запускается...' : 
                                       mcpServerStatus === null ? 'Загрузка...' : 'Запустить MCP сервер'}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      </TabsContent>

                      <TabsContent value="client" className="space-y-6 mt-6">
                        <McpClientManagement />
                      </TabsContent>
                    </Tabs>
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Диалог для создания/редактирования типа объекта */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem?.id ? "Редактировать тип объекта" : "Добавить тип объекта"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Название</Label>
              <Input
                id="name"
                value={editingItem?.name || ""}
                onChange={(e) => setEditingItem((prev: any) => ({ ...prev, name: e.target.value }))}
                placeholder="Введите название типа"
              />
            </div>
            <div>
              <Label htmlFor="description">Описание</Label>
              <Textarea
                id="description"
                value={editingItem?.description || ""}
                onChange={(e) => setEditingItem((prev: any) => ({ ...prev, description: e.target.value }))}
                placeholder="Введите описание типа"
              />
            </div>
            <div>
              <Label htmlFor="parentId">Родительский тип</Label>
              <Select 
                value={editingItem?.parent_id?.toString() || "none"} 
                onValueChange={(value) => setEditingItem((prev: any) => ({ 
                  ...prev, 
                  parent_id: value === "none" ? null : parseInt(value) 
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите родительский тип" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Корневой тип</SelectItem>
                  {objectTypesHierarchy
                    .filter(type => type.id !== editingItem?.id)
                    .map(type => (
                      <SelectItem 
                        key={type.id} 
                        value={type.id.toString()}
                        className="whitespace-pre"
                      >
                        {type.displayName}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsDialogOpen(false);
              setEditingItem(null);
            }}>
              Отмена
            </Button>
            <Button onClick={() => {
              if (editingItem?.id) {
                updateObjectTypeMutation.mutate({ 
                  id: editingItem.id, 
                  data: {
                    name: editingItem.name,
                    description: editingItem.description,
                    parent_id: editingItem.parent_id
                  }
                });
              } else {
                createObjectTypeMutation.mutate({
                  name: editingItem.name,
                  description: editingItem.description,
                  parent_id: editingItem.parent_id
                });
              }
            }}>
              {editingItem?.id ? "Обновить" : "Создать"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Диалог для создания/редактирования типа организации - УДАЛЕН (типы организаций теперь не используются) */}

      {/* Диалог для создания/редактирования организации - УДАЛЕН (организации управляются через KLS) */}

      {/* MCP Геопространственные данные диалог */}
      <Dialog open={isMcpGeoDialogOpen} onOpenChange={setIsMcpGeoDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Настройка геопространственных данных MCP</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-4">
              <h4 className="font-medium">Доступные операции</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox id="geo-read" defaultChecked />
                  <Label htmlFor="geo-read">Чтение кадастровых данных</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="geo-search" defaultChecked />
                  <Label htmlFor="geo-search">Поиск по геометрии</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="geo-export" defaultChecked />
                  <Label htmlFor="geo-export">Экспорт в различные форматы</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="geo-analysis" />
                  <Label htmlFor="geo-analysis">Пространственный анализ</Label>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium">Ограничения доступа</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="geo-max-results">Максимум результатов</Label>
                  <Input id="geo-max-results" type="number" defaultValue="1000" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="geo-rate-limit">Запросов в минуту</Label>
                  <Input id="geo-rate-limit" type="number" defaultValue="60" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="geo-allowed-formats">Разрешенные форматы экспорта</Label>
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center space-x-2">
                  <Checkbox id="format-geojson" defaultChecked />
                  <Label htmlFor="format-geojson">GeoJSON</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="format-kml" defaultChecked />
                  <Label htmlFor="format-kml">KML</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="format-shp" />
                  <Label htmlFor="format-shp">Shapefile</Label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMcpGeoDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={() => {
              toast({
                title: "Настройки сохранены",
                description: "Конфигурация геопространственных данных MCP обновлена"
              });
              setIsMcpGeoDialogOpen(false);
            }}>
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MCP Управление объектами диалог */}
      <Dialog open={isMcpObjectsDialogOpen} onOpenChange={setIsMcpObjectsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Настройка управления объектами MCP</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-4">
              <h4 className="font-medium">CRUD операции</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox id="obj-create" defaultChecked />
                  <Label htmlFor="obj-create">Создание объектов</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="obj-read" defaultChecked />
                  <Label htmlFor="obj-read">Чтение объектов</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="obj-update" defaultChecked />
                  <Label htmlFor="obj-update">Обновление объектов</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="obj-delete" />
                  <Label htmlFor="obj-delete">Удаление объектов</Label>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium">Разрешения по типам объектов</h4>
              <div className="space-y-2">
                <Label>Доступные типы объектов</Label>
                <div className="max-h-32 overflow-y-auto space-y-2">
                  {objectTypesHierarchy.map(type => (
                    <div 
                      key={type.id} 
                      className="flex items-center space-x-2"
                      style={{ paddingLeft: `${type.indentLevel * 20}px` }}
                    >
                      <Checkbox id={`obj-type-${type.id}`} defaultChecked />
                      <Label htmlFor={`obj-type-${type.id}`} className="text-sm whitespace-pre">
                        {type.displayName}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="obj-batch-size">Размер пакета операций</Label>
                <Input id="obj-batch-size" type="number" defaultValue="100" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="obj-timeout">Таймаут операции (сек)</Label>
                <Input id="obj-timeout" type="number" defaultValue="30" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMcpObjectsDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={() => {
              toast({
                title: "Настройки сохранены",
                description: "Конфигурация управления объектами MCP обновлена"
              });
              setIsMcpObjectsDialogOpen(false);
            }}>
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MCP Аналитика и отчеты диалог */}
      <Dialog open={isMcpAnalyticsDialogOpen} onOpenChange={setIsMcpAnalyticsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Настройка аналитики и отчетов MCP</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-4">
              <h4 className="font-medium">Типы отчетов</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox id="report-stats" defaultChecked />
                  <Label htmlFor="report-stats">Статистические отчеты</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="report-geo" defaultChecked />
                  <Label htmlFor="report-geo">Геопространственная аналитика</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="report-usage" defaultChecked />
                  <Label htmlFor="report-usage">Отчеты по использованию</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="report-custom" />
                  <Label htmlFor="report-custom">Пользовательские запросы</Label>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium">Форматы экспорта отчетов</h4>
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center space-x-2">
                  <Checkbox id="export-json" defaultChecked />
                  <Label htmlFor="export-json">JSON</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="export-csv" defaultChecked />
                  <Label htmlFor="export-csv">CSV</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="export-excel" defaultChecked />
                  <Label htmlFor="export-excel">Excel</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="export-pdf" />
                  <Label htmlFor="export-pdf">PDF</Label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="report-max-results">Максимум результатов в отчете</Label>
                <Input id="report-max-results" type="number" defaultValue="10000" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="report-cache-ttl">Время кеширования (минуты)</Label>
                <Input id="report-cache-ttl" type="number" defaultValue="60" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMcpAnalyticsDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={() => {
              toast({
                title: "Настройки сохранены",
                description: "Конфигурация аналитики и отчетов MCP обновлена"
              });
              setIsMcpAnalyticsDialogOpen(false);
            }}>
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global confirmation dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
